import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { initFeatherIcons, getDoctorDashboard, logoutUser, api } from '../../utils/api';
import DoctorAvailability from '../../components/DoctorAvailability';
import DoctorProfile from '../../components/DoctorProfile';
import PrescriptionList from '../../components/prescription/PrescriptionList';
import { getLogoUrl } from '../../utils/logoUtils';
import '../../styles/Dashboard.css';
import './DoctorDashboard.css';

const DoctorDashboard = () => {
  console.log('🏥 DOCTOR DASHBOARD COMPONENT MOUNTED');
  // #region agent log
  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:component-mount',message:'Doctor Dashboard component mounted',data:{timestamp:new Date().toISOString()},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'MOUNT'})}).catch(()=>{});
  // #endregion agent log
  
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [logoUrl, setLogoUrl] = useState(getLogoUrl());
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showLabTests, setShowLabTests] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showPatientDetails, setShowPatientDetails] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profilePictureError, setProfilePictureError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);
  const [earningsData, setEarningsData] = useState(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [earningsError, setEarningsError] = useState(null);
  const [earningsDateFilter, setEarningsDateFilter] = useState({ startDate: '', endDate: '' });
  
  console.log('🏥 Doctor Dashboard State:', { user: user?.name, loading, error, hasDashboardData: !!dashboardData });

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
    setLogoUrl(getLogoUrl());
  };

  // Monitor theme changes and update logo
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
      setLogoUrl(getLogoUrl());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, []);

  // Initialize theme and ensure session persistence
  useEffect(() => {
    // Ensure token is set in API headers on mount/refresh
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // #region agent log
      const hasAuthHeader = !!api.defaults.headers.common['Authorization'];
      const logData = {
        location: 'DoctorDashboard.js:useEffect-mount',
        message: 'Component mounted - setting auth header',
        data: {
          hasToken: !!token,
          tokenLength: token?.length,
          hasAuthHeader: hasAuthHeader
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'DD1'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      }).catch(() => {});
      // #endregion
    } else {
      // #region agent log
      const localStorageKeys = Object.keys(localStorage);
      const logData2 = {
        location: 'DoctorDashboard.js:useEffect-mount-notoken',
        message: 'Component mounted - NO TOKEN',
        data: { localStorageKeys: localStorageKeys },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'DD2'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData2)
      }).catch(() => {});
      // #endregion
    }
    
    const savedTheme = localStorage.getItem('theme') || 'light';
    setIsDarkMode(savedTheme === 'dark');
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const showNotification = (message, type = 'info') => {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification-${type}`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 3000);
  };

  const handleLogout = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm('Are you sure you want to logout?');
    if (!confirmed) return;
    
    try {
      await logoutUser();
      showNotification('Logged out successfully', 'success');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      showNotification('Error during logout. Please try again.', 'error');
    }
  };

  const handleRetryProfilePicture = () => {
    setProfilePictureError(false);
    if (profileData?.profilePicture) {
      // Force re-render of the image
      const img = new Image();
      img.onload = () => setProfilePictureError(false);
      img.onerror = () => setProfilePictureError(true);
      img.src = profileData.profilePicture;
    }
  };

  const handleProfilePictureUpdate = (newProfilePictureUrl) => {
    // Update the profile data with the new picture URL
    setProfileData(prev => ({
      ...prev,
      profilePicture: newProfilePictureUrl
    }));
    // Reset any profile picture errors
    setProfilePictureError(false);
  };

  const handleStartVideoCall = (appointmentId) => {
    navigate(`/video-call/${appointmentId}`);
  };

  const handleCreatePrescription = (appointmentId) => {
    navigate(`/prescription/create/${appointmentId}`);
  };

  const handleShowPrescriptions = () => {
    setShowPrescriptions(!showPrescriptions);
    setShowAvailability(false);
    setShowProfile(false);
    setShowLabTests(false);
    setShowEarnings(false);
  };

  const handleShowLabTests = () => {
    setShowLabTests(!showLabTests);
    setShowAvailability(false);
    setShowProfile(false);
    setShowPrescriptions(false);
    setShowEarnings(false);
  };

  const fetchEarningsData = async () => {
    try {
      setEarningsLoading(true);
      setEarningsError(null);
      const params = new URLSearchParams();
      if (earningsDateFilter.startDate) {
        params.append('startDate', earningsDateFilter.startDate);
      }
      if (earningsDateFilter.endDate) {
        params.append('endDate', earningsDateFilter.endDate);
      }
      const queryString = params.toString();
      const response = await api.get(`/doctor/earnings${queryString ? `?${queryString}` : ''}`);
      if (response.data.success) {
        setEarningsData(response.data.data);
      } else {
        setEarningsError(response.data.message || 'Error fetching earnings data');
      }
    } catch (err) {
      console.error('Error fetching earnings:', err);
      setEarningsError(err.response?.data?.message || 'Error fetching earnings data');
    } finally {
      setEarningsLoading(false);
    }
  };

  const handleShowEarnings = () => {
    const newShowEarnings = !showEarnings;
    setShowEarnings(newShowEarnings);
    setShowAvailability(false);
    setShowProfile(false);
    setShowPrescriptions(false);
    setShowLabTests(false);
    if (newShowEarnings && !earningsData) {
      fetchEarningsData();
    }
  };

  const handleEarningsDateFilter = () => {
    fetchEarningsData();
  };

  const handleViewPatientDetails = async (appointment) => {
    try {
      console.log('Appointment data:', appointment); // Debug log
      
      // Show loading state
      showNotification('Loading patient details...', 'info');
      
      // Get patient ID from appointment
      const patientId = appointment.patientId || appointment.patient?._id;
      console.log('Patient ID extracted:', patientId); // Debug log
      
      if (!patientId) {
        showNotification('Patient ID not found in appointment data', 'error');
        console.error('No patient ID found in appointment:', appointment);
        return;
      }

      // Fetch patient details from API
      console.log('Making API call to:', `/api/doctor/patient/${patientId}`); // Debug log
      const response = await fetch(`/api/doctor/patient/${patientId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('API response status:', response.status); // Debug log

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API response data:', data); // Debug log
      
      if (data.success) {
        setSelectedPatient(data.data);
        setShowPatientDetails(true);
        showNotification('Patient details loaded successfully', 'success');
      } else {
        throw new Error(data.message || 'Failed to fetch patient details');
      }
    } catch (error) {
      console.error('Error fetching patient details:', error);
      showNotification(`Error loading patient details: ${error.message}`, 'error');
      
      // Fallback to basic appointment data if API fails
      const fallbackPatient = {
        id: appointment._id || Math.random().toString(36).substr(2, 9),
        name: appointment.patient || 'Unknown Patient',
        type: appointment.type || 'General Consultation',
        date: appointment.date || new Date().toLocaleDateString(),
        time: appointment.time || 'Not specified',
        status: appointment.status || 'Scheduled',
        phone: appointment.patientPhone || '+1 (555) 0123',
        email: appointment.patientEmail || 'patient@email.com',
        age: appointment.patientAge || 'Not specified',
        gender: appointment.patientGender || 'Not specified',
        bloodGroup: 'Not specified',
        allergies: 'Not specified',
        lastVisit: 'Not available',
        reasonForVisit: appointment.reason || 'General consultation',
        symptoms: appointment.symptoms || ['Not specified'],
        medicalHistory: 'Not available'
      };
      
      setSelectedPatient(fallbackPatient);
      setShowPatientDetails(true);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchDashboardData-start',message:'Starting to fetch dashboard data',data:{},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'D1'})}).catch(()=>{});
      // #endregion agent log
      console.log('Starting to fetch dashboard data...'); // Debug log
      setLoading(true);
      const response = await getDoctorDashboard();
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchDashboardData-response',message:'Dashboard API response received',data:{success:response?.success,hasData:!!response?.data,hasDoctorData:!!response?.data?.doctorData,appointmentsToday:response?.data?.doctorData?.appointmentsToday,patientQueueWaiting:response?.data?.doctorData?.patientQueue?.waiting,scheduleLength:response?.data?.doctorData?.schedule?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'D2'})}).catch(()=>{});
      // #endregion agent log
      console.log('Dashboard API response:', response); // Debug log
      if (response.success) {
        setDashboardData(response.data);
        setError(null);
        setLoading(false); // Fix: Set loading to false on success
        console.log('Dashboard data loaded successfully'); // Debug log
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchDashboardData-success',message:'Dashboard data set successfully',data:{dataKeys:Object.keys(response.data),doctorDataKeys:response.data.doctorData?Object.keys(response.data.doctorData):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'D3'})}).catch(()=>{});
        // #endregion agent log
      } else {
        setError(response.message || 'Failed to fetch dashboard data');
        setLoading(false);
        console.log('Dashboard API failed:', response.message); // Debug log
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchDashboardData-failed',message:'Dashboard API failed',data:{errorMessage:response.message},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'D4'})}).catch(()=>{});
        // #endregion agent log
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.response?.data?.message || 'Error loading dashboard data');
      setLoading(false);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchDashboardData-error',message:'Dashboard fetch error',data:{errorMessage:err?.message,errorStatus:err?.response?.status},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'D5'})}).catch(()=>{});
      // #endregion agent log
    }
  };

  useEffect(() => {
    if (user) {
      // Test backend connectivity first
      const testBackend = async () => {
        try {
          console.log('Testing backend connectivity...');
          const response = await fetch('https://weheal-backend.onrender.com/api/auth/check-auth', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          console.log('Backend connectivity test response:', response.status);
        } catch (error) {
          console.error('Backend connectivity test failed:', error);
        }
      };
      
      testBackend();
      fetchDashboardData();
      fetchProfileData();
    }
  }, [user]);

  // Auto-refresh appointments data every 30 seconds to get updated payment status
  useEffect(() => {
    if (!user || !dashboardData) return;

    const intervalId = setInterval(() => {
      fetchDashboardData();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId);
  }, [user, dashboardData]);

  // Helper function to construct proper profile picture URL
  const constructProfilePictureUrl = (picturePath) => {
    if (!picturePath) return null;
    
    // If it's already a full URL, return as is
    if (picturePath.startsWith('http') || picturePath.startsWith('data:')) {
      return picturePath;
    }
    
    // If it's a relative path starting with /uploads, use the proxy
    // The backend now returns /uploads/profiles/filename and the proxy should handle this
    if (picturePath.startsWith('/uploads/')) {
      return picturePath; // Use the proxy which is set to https://weheal-backend.onrender.com
    }
    
    // Fallback: construct the full URL
    const baseUrl = process.env.REACT_APP_API_URL || 'https://weheal-backend.onrender.com';
    const cleanPath = picturePath.replace(/^\//, ''); // Remove leading slash
    return `${baseUrl}/${cleanPath}`;
  };

  // Helper function to construct patient profile picture URL
  const constructPatientProfilePictureUrl = (picturePath) => {
    if (!picturePath) return null;
    
    // If it's already a full URL (Cloudinary), return as is
    if (picturePath.startsWith('http') || picturePath.startsWith('data:')) {
      return picturePath;
    }
    
    // If it's a relative path starting with /uploads, use the proxy
    if (picturePath.startsWith('/uploads/')) {
      return picturePath;
    }
    
    // Fallback: construct the full URL
    const baseUrl = process.env.REACT_APP_API_URL || 'https://weheal-backend.onrender.com';
    const cleanPath = picturePath.replace(/^\//, '');
    return `${baseUrl}/${cleanPath}`;
  };

  const fetchProfileData = async () => {
    try {
      // Reset error state when fetching new data
      setProfilePictureError(false);
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchProfileData-start',message:'Starting profile fetch',data:{apiBaseUrl:api.defaults.baseURL,hasToken:!!localStorage.getItem('token'),hostname:window.location.hostname},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-fetch-1',hypothesisId:'PF1'})}).catch(()=>{});
      // #endregion agent log
      
      // Use api instance instead of raw fetch to ensure correct base URL
      const response = await api.get('/doctor/profile');
      
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchProfileData-response',message:'Profile API response received',data:{success:response?.data?.success,status:response?.status,hasData:!!response?.data?.data,responseDataType:typeof response?.data,responseDataKeys:response?.data?Object.keys(response.data):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-fetch-1',hypothesisId:'PF2'})}).catch(()=>{});
      // #endregion agent log
      
      if (response.data.success) {
        console.log('Raw profile data:', response.data.data); // Debug log
        console.log('Profile picture path from backend:', response.data.data.profilePicture); // Debug log
          
          // Use the helper function to construct proper URL
        const profilePictureUrl = constructProfilePictureUrl(response.data.data.profilePicture);
          console.log('Constructed profile picture URL:', profilePictureUrl); // Debug log
          
          const profileDataWithPicture = {
          ...response.data.data,
            profilePicture: profilePictureUrl
          };
          setProfileData(profileDataWithPicture);
        
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchProfileData-success',message:'Profile data set successfully',data:{hasProfileData:!!profileDataWithPicture,hasProfilePicture:!!profileDataWithPicture.profilePicture},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-fetch-1',hypothesisId:'PF3'})}).catch(()=>{});
        // #endregion agent log
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchProfileData-failed',message:'Profile API failed',data:{message:response?.data?.message},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-fetch-1',hypothesisId:'PF4'})}).catch(()=>{});
        // #endregion agent log
        console.error('Profile API response not ok:', response.status, response.statusText);
      }
    } catch (err) {
      // #region agent log
      const errorData = {
        message: err?.message,
        responseStatus: err?.response?.status,
        responseData: typeof err?.response?.data === 'string' ? err?.response?.data?.substring(0, 200) : err?.response?.data,
        responseText: typeof err?.response?.data === 'string' ? err?.response?.data?.substring(0, 200) : null,
        isHtmlResponse: typeof err?.response?.data === 'string' && err?.response?.data?.includes('<!doctype'),
        configUrl: err?.config?.url,
        configBaseURL: err?.config?.baseURL
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:fetchProfileData-error',message:'Profile fetch error',data:errorData,timestamp:Date.now(),sessionId:'debug-session',runId:'profile-fetch-1',hypothesisId:'PF5'})}).catch(()=>{});
      // #endregion agent log
      console.error('Error fetching profile data:', err);
      setProfilePictureError(true);
    }
  };

  // Patient search functionality
  const performSearch = useCallback((query) => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:performSearch',message:'Search function called',data:{query,queryLength:query?.length,hasDashboardData:!!dashboardData,allAppointmentsCount:dashboardData?.doctorData?.allAppointments?.length,scheduleCount:dashboardData?.doctorData?.schedule?.length},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'H3'})}).catch(()=>{});
    // #endregion agent log
    
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    const searchTerm = query.toLowerCase().trim();
    const results = [];

    // Search through all appointments
    if (dashboardData?.doctorData?.allAppointments) {
      dashboardData.doctorData.allAppointments.forEach((appointment) => {
        const patientName = (appointment.patient || '').toLowerCase();
        const patientPhone = (appointment.patientPhone || '').toLowerCase();
        const patientEmail = (appointment.patientEmail || '').toLowerCase();

        if (patientName.includes(searchTerm) || patientPhone.includes(searchTerm) || patientEmail.includes(searchTerm)) {
          const existingIndex = results.findIndex((r) => r.patientId === (appointment.patientId || appointment.patient?._id));
          if (existingIndex === -1) {
            results.push({
              patientId: appointment.patientId || appointment.patient?._id,
              name: appointment.patient || 'Unknown Patient',
              phone: appointment.patientPhone || 'N/A',
              email: appointment.patientEmail || 'N/A',
              profilePicture: appointment.patientProfilePicture, // Include profile picture
              lastAppointment: appointment.date,
              lastAppointmentTime: appointment.time,
              appointmentId: appointment._id,
              status: appointment.status,
              type: appointment.type,
            });
          }
        }
      });
    }

    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:performSearch-results',message:'Search results found',data:{resultsCount:results.length,searchTerm},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'H4'})}).catch(()=>{});
    // #endregion agent log

    setSearchResults(results.slice(0, 10));
    setShowSearchResults(results.length > 0);
    setIsSearching(false);
  }, [dashboardData]);

  // Handle search input change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  // Handle click outside to close search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle search result click
  const handleSearchResultClick = async (result) => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:handleSearchResultClick',message:'Search result clicked',data:{patientId:result.patientId,patientName:result.name},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'H5'})}).catch(()=>{});
    // #endregion agent log
    
    try {
      setShowSearchResults(false);
      setSearchQuery('');
      
      const response = await fetch(`/api/doctor/patient/${result.patientId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSelectedPatient(data.data);
          setShowPatientDetails(true);
          showNotification('Patient details loaded', 'success');
        }
      } else {
        // Fallback
        setSelectedPatient({
          _id: result.patientId,
          name: result.name,
          phone: result.phone,
          email: result.email,
          appointments: []
        });
        setShowPatientDetails(true);
      }
    } catch (error) {
      console.error('Error:', error);
      showNotification('Error loading patient details', 'error');
    }
  };

  // Handle search input focus
  const handleSearchFocus = () => {
    if (searchResults.length > 0) {
      setShowSearchResults(true);
    }
  };

  useEffect(() => {
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:306',message:'Feather icons initialization',data:{hasDashboardData:!!dashboardData,searchResultsCount:searchResults.length,showSearchResults,iconElementsCount:document.querySelectorAll('[data-feather]').length,dashboardDataKeys:dashboardData?Object.keys(dashboardData):[],doctorDataKeys:dashboardData?.doctorData?Object.keys(dashboardData.doctorData):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion agent log
    initFeatherIcons();
    // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:308',message:'After feather icons init',data:{iconElementsAfter:document.querySelectorAll('[data-feather]').length,svgElementsCount:document.querySelectorAll('svg.feather').length,overviewPanelExists:!!document.querySelector('.doctor-overview-content-modern'),statCardsCount:document.querySelectorAll('.stat-card-overview').length,greetingExists:!!document.querySelector('.greeting-section-modern'),quickActionsCount:document.querySelectorAll('.quick-action-card').length,scheduleCardsCount:document.querySelectorAll('.schedule-card-modern').length},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'H2'})}).catch(()=>{});
    // #endregion agent log
  }, [dashboardData, searchResults, showSearchResults]);

  // Check DOM after render
  useEffect(() => {
    if (dashboardData && !showAvailability && !showProfile && !showPrescriptions && !showLabTests && !showEarnings) {
      const timer = setTimeout(() => {
        const overviewPanel = document.querySelector('.doctor-overview-content-modern');
        const statCards = document.querySelectorAll('.stat-card-overview');
        const statsGrid = document.querySelector('.overview-stats-grid');
        const statValues = Array.from(statCards).map(card => {
          const valueEl = card.querySelector('.stat-card-overview-value');
          const labelEl = card.querySelector('.stat-card-overview-label');
          return {
            value: valueEl?.textContent?.trim(),
            label: labelEl?.textContent?.trim(),
            isVisible: valueEl && window.getComputedStyle(valueEl).display !== 'none',
            computedDisplay: valueEl ? window.getComputedStyle(valueEl).display : null,
            computedVisibility: valueEl ? window.getComputedStyle(valueEl).visibility : null
          };
        });
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DoctorDashboard.js:DOM-check',message:'DOM check after render',data:{overviewPanelExists:!!overviewPanel,statCardsCount:statCards.length,statsGridExists:!!statsGrid,statsGridChildren:statsGrid?.children?.length,statValues,overviewPanelDisplay:overviewPanel?window.getComputedStyle(overviewPanel).display:null,overviewPanelVisibility:overviewPanel?window.getComputedStyle(overviewPanel).visibility:null},timestamp:Date.now(),sessionId:'debug-session',runId:'doctor-dash-1',hypothesisId:'DOM1'})}).catch(()=>{});
        // #endregion agent log
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [dashboardData, showAvailability, showProfile, showPrescriptions, showLabTests, showEarnings]);

  if (!user) {
    return (
      <div className="auth-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auth-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">{error}</div>
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Retry
        </button>
      </div>
    );
  }

  if (!dashboardData || !dashboardData.user) {
    return <div className="error">No dashboard data available</div>;
  }

  return (
    <div className="doctor-dashboard-modern">
      {/* Top Navigation Bar */}
      <div className="doctor-navbar-modern">
        <div className="navbar-left-modern">
          <div className="logo-section-modern">
            <img 
              src={logoUrl} 
              alt="WeHeal" 
              className="logo-image-modern"
              style={{ height: '48px', width: 'auto', objectFit: 'contain', marginRight: '12px' }}
              onError={(e) => {
                // Fallback to icon if image fails
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                if (parent && !parent.querySelector('.fallback-logo-icon-modern')) {
                  const fallback = document.createElement('div');
                  fallback.className = 'fallback-logo-icon-modern logo-icon-modern';
                  fallback.innerHTML = '<i data-feather="activity"></i>';
                  parent.insertBefore(fallback, e.target);
                  if (window.feather) window.feather.replace();
                }
              }}
            />
            <div>
              <h1 className="logo-title-modern">WeHeal</h1>
              <p className="logo-subtitle-modern">Doctor Portal</p>
            </div>
          </div>
        </div>

        <div className="navbar-center-modern">
          <div className="search-container-modern">
            <div className="search-modern">
              <i data-feather="search" className="search-icon-modern"></i>
          <input 
                ref={searchInputRef}
            type="text" 
                placeholder="Search patients by name, phone, or email..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                className="search-input-modern"
              />
              {searchQuery && (
          <button 
                  className="search-clear-btn"
                  onClick={() => {
                    setSearchQuery('');
                    setSearchResults([]);
                    setShowSearchResults(false);
                  }}
                  title="Clear search"
                >
                  <i data-feather="x"></i>
          </button>
              )}
              {isSearching && (
                <div className="search-loading-spinner"></div>
              )}
        </div>
            
            {/* Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
              <div ref={searchResultsRef} className="search-results-dropdown">
                <div className="search-results-header">
                  <span className="search-results-count">
                    {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} found
                  </span>
                </div>
                <div className="search-results-list">
                  {searchResults.map((result, index) => (
                    <div
                      key={`${result.patientId}-${index}`}
                      className="search-result-item"
                      onClick={() => handleSearchResultClick(result)}
                    >
                      <div className="search-result-avatar">
                        {result.profilePicture ? (
                          <img 
                            src={constructPatientProfilePictureUrl(result.profilePicture)} 
                            alt={result.name}
                            className="search-result-avatar-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="search-result-avatar-placeholder" style={{ display: result.profilePicture ? 'none' : 'flex' }}>
                          <i data-feather="user"></i>
                        </div>
                      </div>
                      <div className="search-result-content">
                        <div className="search-result-name">{result.name}</div>
                        <div className="search-result-details">
                          {result.phone !== 'N/A' && (
                            <span className="search-result-detail">
                              <i data-feather="phone"></i>
                              {result.phone}
                            </span>
                          )}
                          {result.email !== 'N/A' && (
                            <span className="search-result-detail">
                              <i data-feather="mail"></i>
                              {result.email}
                            </span>
                          )}
                        </div>
                        {result.lastAppointment && (
                          <div className="search-result-appointment">
                            <i data-feather="calendar"></i>
                            Last visit: {result.lastAppointment} {result.lastAppointmentTime && `at ${result.lastAppointmentTime}`}
                          </div>
                        )}
                      </div>
                      <div className="search-result-arrow">
                        <i data-feather="chevron-right"></i>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
              <div ref={searchResultsRef} className="search-results-dropdown">
                <div className="search-no-results">
                  <i data-feather="search"></i>
                  <p>No patients found</p>
                  <span>Try searching with a different name, phone, or email</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="navbar-right-modern">
          <button 
            className="theme-toggle-btn-modern"
            onClick={toggleDarkMode}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            <i data-feather={isDarkMode ? 'sun' : 'moon'}></i>
          </button>
          <div className="profile-section-modern">
          {profileData?.profilePicture && !profilePictureError ? (
            <img 
              src={profileData.profilePicture} 
              alt="Profile" 
                className="profile-avatar-modern"
              onError={(e) => {
                console.error('Error loading profile picture:', e);
                setProfilePictureError(true);
              }}
            />
          ) : (
              <div className="profile-avatar-placeholder-modern">
                <i data-feather="user"></i>
              </div>
            )}
            <div className="profile-info-modern">
              <span className="profile-name-modern">Dr. {user.name}</span>
              <span className="profile-role-modern">Doctor</span>
            </div>
          </div>
          <button className="logout-btn-modern" onClick={handleLogout} title="Logout">
            <i data-feather="log-out"></i>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="doctor-dashboard-content-modern">
        {/* Quick Actions Bar */}
        <div className="quick-actions-bar-modern">
        <button 
            className="quick-action-btn-modern primary"
          onClick={() => showNotification('Starting consultation...', 'info')}
        >
          <i data-feather="video"></i>
            <span>Start Consultation</span>
        </button>
        <button 
            className="quick-action-btn-modern"
          onClick={() => {
            setShowAvailability(!showAvailability);
            setShowProfile(false);
            setShowPrescriptions(false);
            setShowLabTests(false);
            setShowEarnings(false);
          }}
        >
          <i data-feather="clock"></i>
            <span>Availability</span>
        </button>
        <button 
            className="quick-action-btn-modern"
          onClick={() => {
            setShowProfile(!showProfile);
            setShowAvailability(false);
            setShowPrescriptions(false);
            setShowLabTests(false);
            setShowEarnings(false);
          }}
        >
          <i data-feather="user"></i>
            <span>Profile</span>
        </button>
        <button 
            className="quick-action-btn-modern"
          onClick={handleShowPrescriptions}
        >
          <i data-feather="file-text"></i>
            <span>Prescriptions</span>
        </button>
        <button 
            className="quick-action-btn-modern"
          onClick={handleShowLabTests}
        >
          <i data-feather="clipboard"></i>
            <span>Lab Tests</span>
        </button>
        <button 
            className="quick-action-btn-modern"
          onClick={handleShowEarnings}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            <text x="12" y="18" textAnchor="middle" fontSize="16" fill="currentColor" fontFamily="Arial, sans-serif" fontWeight="bold">৳</text>
          </svg>
            <span>Earnings</span>
        </button>
      </div>

        {/* Medical Insights Banner - Modern */}
        <div className="medical-insights-banner-modern">
          <div className="insights-content-modern">
            <div className="insights-icon-modern">
              <i data-feather="activity"></i>
            </div>
            <div className="insights-text-modern">
              <h3>Today's Overview</h3>
              <p>
                <strong>{dashboardData?.doctorData?.patientQueue?.waiting || 0}</strong> patients waiting • 
                <strong> {dashboardData?.doctorData?.appointmentsToday || 0}</strong> appointments scheduled
              </p>
            </div>
          </div>
        </div>

      {/* Main Dashboard Content - Modern Overview Panel */}
      {!showAvailability && !showProfile && !showPrescriptions && !showLabTests && !showEarnings && (
        <div className="doctor-overview-content-modern">

          {/* Greeting Section */}
          <div className="greeting-section-modern">
            <h1 className="greeting-title-modern">Welcome, Dr. {user?.name?.split(' ')[0] || 'Doctor'}!</h1>
            <p className="greeting-subtitle-modern">Here's your practice overview for today</p>
          </div>

          {/* Stats Grid - Key Metrics */}
          <div className="overview-stats-grid">
            <div className="stat-card-overview">
              <div className="stat-card-overview-icon primary">
                <i data-feather="users"></i>
              </div>
              <div className="stat-card-overview-content">
                <span className="stat-card-overview-label">Patient Queue</span>
                <span className="stat-card-overview-value">{dashboardData?.doctorData?.patientQueue?.waiting || 0}</span>
              </div>
              <div className="stat-card-overview-footer">
                <p className="stat-card-overview-footer-text">Patients waiting</p>
              </div>
            </div>

            <div className="stat-card-overview">
              <div className="stat-card-overview-icon success">
                <i data-feather="calendar"></i>
              </div>
              <div className="stat-card-overview-content">
                <span className="stat-card-overview-label">Today's Appointments</span>
                <span className="stat-card-overview-value">{dashboardData?.doctorData?.appointmentsToday || 0}</span>
              </div>
              <div className="stat-card-overview-footer">
                <p className="stat-card-overview-footer-text">Scheduled today</p>
              </div>
            </div>

            <div className="stat-card-overview">
              <div className="stat-card-overview-icon warning">
                <i data-feather="file-text"></i>
              </div>
              <div className="stat-card-overview-content">
                <span className="stat-card-overview-label">Prescriptions</span>
                <span className="stat-card-overview-value">{dashboardData?.doctorData?.prescriptionsToday?.completed || 0}</span>
              </div>
              <div className="stat-card-overview-footer">
                <p className="stat-card-overview-footer-text">Completed today</p>
              </div>
            </div>

            <div className="stat-card-overview">
              <div className="stat-card-overview-icon info">
                <i data-feather="message-circle"></i>
              </div>
              <div className="stat-card-overview-content">
                <span className="stat-card-overview-label">Messages</span>
                <span className="stat-card-overview-value">{dashboardData?.doctorData?.messages?.unread || 0}</span>
              </div>
              <div className="stat-card-overview-footer">
                <p className="stat-card-overview-footer-text">Unread messages</p>
              </div>
            </div>
          </div>

          {/* Quick Actions Grid */}
          <div className="quick-actions-section-modern">
            <h3 className="section-title-modern">Quick Actions</h3>
            <div className="quick-action-grid">
              <button 
                className="quick-action-card"
                onClick={() => showNotification('Starting consultation...', 'info')}
              >
                <div className="action-icon">🎥</div>
                <h3>Start Consultation</h3>
                <p>Begin video call</p>
              </button>
              
              <button 
                className="quick-action-card"
                onClick={() => showNotification('Opening schedule...', 'info')}
              >
                <div className="action-icon">📅</div>
                <h3>View Schedule</h3>
                <p>See all appointments</p>
              </button>
              
              <button 
                className="quick-action-card"
                onClick={() => {
                  setShowAvailability(!showAvailability);
                  setShowProfile(false);
                  setShowPrescriptions(false);
                  setShowLabTests(false);
                  setShowEarnings(false);
                }}
              >
                <div className="action-icon">⏰</div>
                <h3>Manage Availability</h3>
                <p>Set your hours</p>
              </button>
              
              <button 
                className="quick-action-card"
                onClick={handleShowPrescriptions}
              >
                <div className="action-icon">📋</div>
                <h3>Prescriptions</h3>
                <p>View & create</p>
              </button>
              
              <button 
                className="quick-action-card"
                onClick={handleShowLabTests}
              >
                <div className="action-icon">🧪</div>
                <h3>Lab Tests</h3>
                <p>Assigned tests</p>
              </button>
              
              <button 
                className="quick-action-card"
                onClick={handleShowEarnings}
              >
                <div className="action-icon">💰</div>
                <h3>Earnings</h3>
                <p>View analytics</p>
              </button>
            </div>
          </div>

          {/* Today's Schedule - Redesigned */}
          <div className="todays-schedule-section-modern">
            <div className="schedule-header-redesigned">
              <div className="schedule-header-left">
                <div className="schedule-icon-wrapper">
                  <i data-feather="calendar"></i>
                </div>
                <div>
                  <h2 className="schedule-title-redesigned">Today's Schedule</h2>
                  <p className="schedule-date-redesigned">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="schedule-header-right">
                <div className="schedule-count-badge">
                  <i data-feather="users"></i>
                  <span>{dashboardData?.doctorData?.schedule?.length || 0} Appointments</span>
                </div>
              </div>
            </div>

            {(!dashboardData?.doctorData?.schedule || dashboardData.doctorData.schedule.length === 0) ? (
              <div className="no-schedule-today-modern">
                <div className="no-schedule-icon">
                  <i data-feather="check-circle"></i>
                </div>
                <h3>All Clear!</h3>
                <p>No appointments scheduled for today</p>
                <span>You're all caught up. Enjoy your day!</span>
              </div>
            ) : (
              <div className="schedule-timeline-modern">
                {dashboardData?.doctorData?.schedule?.map((appointment, index) => {
                  // Format time to AM/PM
                  const formatTimeToAMPM = (time24) => {
                    if (!time24) return 'N/A';
                    const timeParts = time24.split(' - ');
                    if (timeParts.length === 2) {
                      const [start, end] = timeParts;
                      const formatSingle = (t) => {
                        const [hours, minutes] = t.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const hour12 = hour % 12 || 12;
                        return `${hour12}:${minutes} ${ampm}`;
                      };
                      return `${formatSingle(start)} - ${formatSingle(end)}`;
                    }
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${minutes} ${ampm}`;
                  };

                  const formattedTime = formatTimeToAMPM(appointment.time);

                  return (
                    <div key={appointment._id} className="schedule-item-modern">
                      <div className="schedule-timeline-marker">
                        <div className="timeline-dot"></div>
                        {index < (dashboardData?.doctorData?.schedule?.length || 0) - 1 && (
                          <div className="timeline-line"></div>
                        )}
                      </div>
                      <div className="schedule-item-content">
                        <div className="schedule-item-header">
                          <div className="schedule-time-display">
                            <i data-feather="clock"></i>
                            <span className="time-text">{formattedTime}</span>
                          </div>
                          <div className="schedule-item-badges">
                            <span className={`appointment-type-badge-redesigned ${appointment.type?.toLowerCase().replace('-', '') || 'inperson'}`}>
                              <i data-feather={appointment.type === 'Tele-Consult' ? 'video' : 'user'}></i>
                              {appointment.type}
                            </span>
                            <span className={`status-badge-redesigned status-${appointment.status?.toLowerCase().replace(' ', '') || 'scheduled'}`}>
                      {appointment.status}
                    </span>
                            {appointment.consultationPaymentStatus && (
                              <span className={`payment-badge-redesigned payment-${appointment.consultationPaymentStatus}`}>
                                <i data-feather={appointment.consultationPaymentStatus === 'completed' ? 'check-circle' : 
                                                 appointment.consultationPaymentStatus === 'failed' ? 'x-circle' : 'clock'}></i>
                                {appointment.consultationPaymentStatus === 'completed' ? 'Paid' : 
                                 appointment.consultationPaymentStatus === 'failed' ? 'Failed' : 'Pending'}
                              </span>
                            )}
                  </div>
                        </div>
                        <div className="schedule-patient-info">
                          <h3 className="patient-name-redesigned">{appointment.patient}</h3>
                          {appointment.consultationFee && (
                            <div className="consultation-fee-display">
                              <span>Fee: ৳{appointment.consultationFee}</span>
                            </div>
                          )}
                        </div>
                        <div className="schedule-item-actions-redesigned">
                      {appointment.type === 'Tele-Consult' && (
                        <button 
                              className="btn-schedule-action btn-video-redesigned"
                          onClick={() => handleStartVideoCall(appointment._id)}
                          disabled={appointment.videoCallStatus === 'completed'}
                              title="Start/Join Video Call"
                        >
                          <i data-feather="video"></i>
                          {appointment.videoCallStatus === 'in-progress' ? 'Join Call' : 
                               appointment.videoCallStatus === 'completed' ? 'Completed' : 
                           'Start Call'}
                        </button>
                      )}
                      <button 
                            className="btn-schedule-action btn-details-redesigned"
                        onClick={() => handleViewPatientDetails(appointment)}
                            title="View Patient Details"
                      >
                            <i data-feather="user"></i>
                            Details
                      </button>
                      <button 
                            className="btn-schedule-action btn-prescription-redesigned"
                        onClick={() => handleCreatePrescription(appointment._id)}
                        title="Create Prescription"
                      >
                        <i data-feather="file-plus"></i>
                        Prescription
                      </button>
                    </div>
                  </div>
                </div>
                  );
                })}
                </div>
              )}
            </div>

          {/* Additional Statistics */}
          <div className="stats-section-secondary">
            <div className="stat-card-secondary">
              <div className="stat-card-secondary-icon">
                <i data-feather="users"></i>
            </div>
              <div className="stat-card-secondary-content">
                <h4>Total Patients</h4>
                <p className="stat-value">{dashboardData?.doctorData?.totalPatients || 0}</p>
          </div>
            </div>
            <div className="stat-card-secondary">
              <div className="stat-card-secondary-icon">
                <i data-feather="user-plus"></i>
            </div>
              <div className="stat-card-secondary-content">
                <h4>Upcoming Patients</h4>
                <p className="stat-value">{dashboardData?.doctorData?.upcomingAppointments || 0}</p>
              </div>
            </div>
            <div className="stat-card-secondary">
              <div className="stat-card-secondary-icon">
                <i data-feather="check-circle"></i>
              </div>
              <div className="stat-card-secondary-content">
                <h4>Completed</h4>
                <p className="stat-value">{dashboardData?.doctorData?.completedAppointments || 0}</p>
              </div>
            </div>
          </div>

          {/* All Appointments - Modern Redesign */}
          <div className="all-appointments-section-modern">
            <div className="appointments-header-modern">
              <div className="header-content-modern">
                <h2 className="appointments-title-modern">
                  <i data-feather="calendar"></i>
                  All Appointments
                </h2>
                <p className="appointments-subtitle-modern">
                  {dashboardData?.doctorData?.allAppointments?.length || 0} total appointments
                </p>
              </div>
              <div className="header-actions-modern">
                  <input 
                    type="text" 
                  className="appointments-search-modern"
                    placeholder="Search appointments..." 
                    onChange={(e) => showNotification(`Searching for: ${e.target.value}`, 'info')}
                  />
                    <button 
                  className="btn-header-action-modern"
                      title="Refresh" 
                      onClick={() => {
                        showNotification('Refreshing data...', 'info');
                        fetchDashboardData();
                      }}
                    >
                      <i data-feather="refresh-ccw"></i>
                    </button>
                  </div>
                </div>

            {(!dashboardData?.doctorData?.allAppointments || dashboardData.doctorData.allAppointments.length === 0) ? (
              <div className="no-appointments-modern">
                <i data-feather="calendar"></i>
                <p>No appointments scheduled</p>
                <span>All appointments will appear here</span>
              </div>
            ) : (
              <div className="appointments-grid-modern">
                {dashboardData?.doctorData?.allAppointments?.map((appointment) => {
                  // Format date as "11 DEC"
                  const appointmentDate = appointment.appointmentDate ? new Date(appointment.appointmentDate) : new Date(appointment.date);
                  const day = appointmentDate.getDate();
                  const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
                  const month = monthNames[appointmentDate.getMonth()];
                  const formattedDate = `${day} ${month}`;

                  // Format time to AM/PM
                  const formatTimeToAMPM = (time24) => {
                    if (!time24) return 'N/A';
                    const [hours, minutes] = time24.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const hour12 = hour % 12 || 12;
                    return `${hour12}:${minutes} ${ampm}`;
                  };

                  const startTimeAMPM = formatTimeToAMPM(appointment.startTime);
                  const endTimeAMPM = formatTimeToAMPM(appointment.endTime);

                  return (
                    <div key={appointment._id} className="appointment-card-modern">
                      <div className="appointment-date-modern">
                        <div className="date-day-modern">{day}</div>
                        <div className="date-month-modern">{month}</div>
                      </div>
                      <div className="appointment-content-modern">
                        <div className="appointment-time-modern">
                          <i data-feather="clock"></i>
                          <span>{startTimeAMPM} - {endTimeAMPM}</span>
                        </div>
                        <div className="appointment-patient-modern">
                          <h3>{appointment.patient}</h3>
                          <div className="appointment-meta-modern">
                            <span className={`appointment-type-badge-modern ${appointment.type?.toLowerCase().replace('-', '') || 'inperson'}`}>
                              <i data-feather={appointment.type === 'Tele-Consult' ? 'video' : 'user'}></i>
                              {appointment.type}
                            </span>
                            <span className={`status-badge-modern status-${appointment.status?.toLowerCase().replace(' ', '') || 'scheduled'}`}>
                          {appointment.status}
                        </span>
                          </div>
                        </div>
                        <div className="appointment-payment-modern">
                          <div className="payment-info-modern">
                            <span className="payment-label-modern">Fee:</span>
                            <span className="payment-amount-modern">
                              {appointment.consultationFee ? `৳${appointment.consultationFee}` : 'N/A'}
                            </span>
                          </div>
                          <span className={`payment-status-badge-modern payment-${appointment.consultationPaymentStatus || 'pending'}`}>
                            <i data-feather={appointment.consultationPaymentStatus === 'completed' ? 'check-circle' : 
                                             appointment.consultationPaymentStatus === 'failed' ? 'x-circle' : 'clock'}></i>
                          {appointment.consultationPaymentStatus === 'completed' ? 'Paid' : 
                           appointment.consultationPaymentStatus === 'failed' ? 'Failed' : 'Pending'}
                        </span>
                        </div>
                        <div className="appointment-actions-modern">
                          {appointment.type === 'Tele-Consult' && (
                            <button 
                              className="btn-action-card-modern btn-video-modern"
                              onClick={() => handleStartVideoCall(appointment._id)}
                              disabled={appointment.videoCallStatus === 'completed'}
                              title="Start/Join Video Call"
                            >
                              <i data-feather="video"></i>
                              {appointment.videoCallStatus === 'in-progress' ? 'Join' : 
                               appointment.videoCallStatus === 'completed' ? 'Done' : 
                               'Start Call'}
                            </button>
                          )}
                          <button 
                            className="btn-action-card-modern btn-details-modern"
                            onClick={() => handleViewPatientDetails(appointment)}
                            title="View Patient Details"
                          >
                            <i data-feather="user"></i>
                            Details
                          </button>
                          <button 
                            className="btn-action-card-modern btn-prescription-modern"
                            onClick={() => handleCreatePrescription(appointment._id)}
                            title="Create Prescription"
                          >
                            <i data-feather="file-plus"></i>
                            Rx
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>
      )}

      {/* Render Sections */}
      {showAvailability && (
        <div className="section-panel">
          <div className="section-header">
            <h2>Manage Availability</h2>
            <button 
              className="close-btn-red" 
              onClick={() => setShowAvailability(false)}
              title="Close"
            >
              <i data-feather="x"></i>
            </button>
          </div>
          <div className="section-content">
            <DoctorAvailability />
          </div>
        </div>
      )}

      {showProfile && (
        <div className="section-panel">
          <div className="section-header">
            <h2>Edit Profile</h2>
            <button 
              className="close-btn-red" 
              onClick={() => setShowProfile(false)}
              title="Close"
            >
              <i data-feather="x"></i>
            </button>
          </div>
          <div className="section-content">
            <DoctorProfile onProfileUpdate={handleProfilePictureUpdate} />
          </div>
        </div>
      )}

      {showPrescriptions && (
        <div className="section-panel">
          <div className="section-header">
            <h2>My Prescriptions</h2>
            <button 
              className="close-btn-red" 
              onClick={() => setShowPrescriptions(false)}
              title="Close"
            >
              <i data-feather="x"></i>
            </button>
          </div>
          <div className="section-content">
            <PrescriptionList userRole="doctor" />
          </div>
        </div>
      )}

      {showLabTests && (
        <div className="section-panel">
          <div className="section-header">
            <h2>Lab Tests</h2>
            <button 
              className="close-btn-red" 
              onClick={() => setShowLabTests(false)}
              title="Close"
            >
              <i data-feather="x"></i>
            </button>
          </div>
          <div className="section-content">
            <div className="lab-tests-panel">
              <p>Lab test management functionality will be implemented here.</p>
            </div>
          </div>
        </div>
      )}

      {showEarnings && (
        <div className="section-panel earnings-panel-modern">
          <div className="section-header">
            <h2>Earnings & Analytics</h2>
            <button 
              className="close-btn-red" 
              onClick={() => setShowEarnings(false)}
              title="Close"
            >
              <i data-feather="x"></i>
            </button>
          </div>
          <div className="section-content earnings-content-modern">
            {/* Date Filter */}
            <div className="earnings-filter-modern">
              <div className="filter-group-modern">
                <label>Start Date</label>
                <input
                  type="date"
                  value={earningsDateFilter.startDate}
                  onChange={(e) => setEarningsDateFilter(prev => ({ ...prev, startDate: e.target.value }))}
                />
          </div>
              <div className="filter-group-modern">
                <label>End Date</label>
                <input
                  type="date"
                  value={earningsDateFilter.endDate}
                  onChange={(e) => setEarningsDateFilter(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <button 
                className="btn-filter-modern"
                onClick={handleEarningsDateFilter}
                disabled={earningsLoading}
              >
                <i data-feather="filter"></i>
                Apply Filter
              </button>
              <button 
                className="btn-reset-modern"
                onClick={() => {
                  setEarningsDateFilter({ startDate: '', endDate: '' });
                  fetchEarningsData();
                }}
                disabled={earningsLoading}
              >
                <i data-feather="refresh-cw"></i>
                Reset
              </button>
            </div>

            {earningsLoading ? (
              <div className="earnings-loading-modern">
                <div className="loading-spinner"></div>
                <p>Loading earnings data...</p>
              </div>
            ) : earningsError ? (
              <div className="earnings-error-modern">
                <i data-feather="alert-circle"></i>
                <p>{earningsError}</p>
                <button onClick={fetchEarningsData}>Retry</button>
              </div>
            ) : earningsData ? (
              <>
                {/* Statistics Cards */}
                <div className="earnings-stats-grid-modern">
                  <div className="earnings-stat-card-modern primary">
                    <div className="stat-card-icon-modern">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <text x="12" y="18" textAnchor="middle" fontSize="20" fill="currentColor" fontFamily="Arial, sans-serif" fontWeight="bold">৳</text>
                      </svg>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>Total Earnings</h3>
                      <p className="stat-value-modern">
                        {earningsData.currency} {earningsData.statistics.totalEarnings.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="earnings-stat-card-modern success">
                    <div className="stat-card-icon-modern">
                      <i data-feather="calendar"></i>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>Today's Earnings</h3>
                      <p className="stat-value-modern">
                        {earningsData.currency} {earningsData.statistics.todayEarnings.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="earnings-stat-card-modern info">
                    <div className="stat-card-icon-modern">
                      <i data-feather="trending-up"></i>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>This Month</h3>
                      <p className="stat-value-modern">
                        {earningsData.currency} {earningsData.statistics.thisMonthEarnings.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="earnings-stat-card-modern warning">
                    <div className="stat-card-icon-modern">
                      <i data-feather="users"></i>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>Total Appointments</h3>
                      <p className="stat-value-modern">{earningsData.statistics.totalAppointments}</p>
                    </div>
                  </div>
                  <div className="earnings-stat-card-modern secondary">
                    <div className="stat-card-icon-modern">
                      <i data-feather="check-circle"></i>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>Completed</h3>
                      <p className="stat-value-modern">{earningsData.statistics.completedAppointments}</p>
                    </div>
                  </div>
                  <div className="earnings-stat-card-modern accent">
                    <div className="stat-card-icon-modern">
                      <i data-feather="bar-chart-2"></i>
                    </div>
                    <div className="stat-card-content-modern">
                      <h3>Average per Appointment</h3>
                      <p className="stat-value-modern">
                        {earningsData.currency} {earningsData.statistics.averageEarning.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bill Records Table */}
                <div className="earnings-table-container-modern">
                  <h3 className="table-title-modern">
                    <i data-feather="file-text"></i>
                    Appointment Bill Records
                  </h3>
                  {earningsData.billRecords.length === 0 ? (
                    <div className="no-records-modern">
                      <i data-feather="inbox"></i>
                      <p>No bill records found for the selected period</p>
                    </div>
                  ) : (
                    <div className="table-wrapper-modern">
                      <table className="earnings-table-modern">
                        <thead>
                          <tr>
                            <th>Date & Time</th>
                            <th>Patient</th>
                            <th>Appointment Type</th>
                            <th>Consultation Fee</th>
                            <th>Payment Method</th>
                            <th>Transaction ID</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {earningsData.billRecords.map((record) => (
                            <tr key={record.id}>
                              <td>
                                <div className="date-time-cell-modern">
                                  <div className="date-modern">
                                    {record.appointmentDate 
                                      ? new Date(record.appointmentDate).toLocaleDateString()
                                      : record.completedAt 
                                        ? new Date(record.completedAt).toLocaleDateString()
                                        : 'N/A'}
                                  </div>
                                  <div className="time-modern">
                                    {record.appointmentTime || 
                                     (record.completedAt ? new Date(record.completedAt).toLocaleTimeString() : 'N/A')}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="patient-cell-modern">
                                  <div className="patient-name-modern">{record.patientName}</div>
                                  <div className="patient-contact-modern">
                                    {record.patientPhone} | {record.patientEmail}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <span className={`appointment-type-badge-modern ${record.appointmentType?.toLowerCase() || 'unknown'}`}>
                                  {record.appointmentType || 'N/A'}
                                </span>
                              </td>
                              <td className="amount-cell-modern">
                                <strong>{record.currency} {record.amount.toLocaleString()}</strong>
                              </td>
                              <td>
                                <span className="payment-method-badge-modern">
                                  {record.paymentMethod}
                                </span>
                              </td>
                              <td className="transaction-id-modern">
                                {record.transactionId || 'N/A'}
                              </td>
                              <td>
                                <span className={`status-badge-modern status-${record.status}`}>
                                  {record.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
        </div>
      )}
        </div>
              </>
            ) : null}
          </div>
        </div>
      )}
      </div>

      {/* Patient Details Modal */}
      {showPatientDetails && selectedPatient && (
        <div className="modal-overlay-new" onClick={() => setShowPatientDetails(false)}>
          <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-new">
              <h2>Patient Details</h2>
              <button className="close-btn-red" onClick={() => setShowPatientDetails(false)} title="Close">
                <i data-feather="x"></i>
              </button>
            </div>
            <div className="modal-body-new">
              <div className="patient-details-section">
                <div className="patient-header">
                  <div className="patient-avatar">
                    {selectedPatient.profilePicture ? (
                      <img 
                        src={constructPatientProfilePictureUrl(selectedPatient.profilePicture)} 
                        alt={selectedPatient.name}
                        className="patient-avatar-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="avatar-placeholder" style={{ display: selectedPatient.profilePicture ? 'none' : 'flex' }}>
                      <i data-feather="user"></i>
                    </div>
                  </div>
                  <div className="patient-info">
                    <h3>{selectedPatient.name}</h3>
                    <p>Patient ID: #{selectedPatient._id?.slice(-6) || 'N/A'}</p>
                    <span className="status-badge status-active">
                      Active Patient
                    </span>
                  </div>
                </div>

                <div className="patient-details-grid">
                  <div className="detail-section">
                    <h4>Contact Information</h4>
                    <div className="detail-item">
                      <i data-feather="phone"></i>
                      <div>
                        <span className="label">Phone</span>
                        <span className="value">{selectedPatient.phone}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <i data-feather="mail"></i>
                      <div>
                        <span className="label">Email</span>
                        <span className="value">{selectedPatient.email}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Basic Information</h4>
                    <div className="detail-item">
                      <i data-feather="calendar"></i>
                      <div>
                        <span className="label">Age</span>
                        <span className="value">{selectedPatient.age} years</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <i data-feather="user"></i>
                      <div>
                        <span className="label">Gender</span>
                        <span className="value">{selectedPatient.gender}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <i data-feather="droplet"></i>
                      <div>
                        <span className="label">Blood Group</span>
                        <span className="value">{selectedPatient.bloodGroup}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Appointment Details</h4>
                    {selectedPatient.appointments && selectedPatient.appointments.length > 0 ? (
                      selectedPatient.appointments.slice(0, 3).map((apt, index) => (
                        <div key={apt._id} className="detail-item">
                          <i data-feather="calendar"></i>
                          <div>
                            <span className="label">Appointment {index + 1}</span>
                            <span className="value">
                              {new Date(apt.appointmentDate).toLocaleDateString()} - {apt.startTime}
                            </span>
                            <span className="sub-value">Type: {apt.type} | Status: {apt.status}</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="detail-item">
                        <i data-feather="calendar"></i>
                        <div>
                          <span className="label">No Appointments</span>
                          <span className="value">No appointment history found</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>Medical Information</h4>
                    <div className="detail-item">
                      <i data-feather="alert-triangle"></i>
                      <div>
                        <span className="label">Allergies</span>
                        <span className="value">{selectedPatient.patientDetails?.allergies || 'None reported'}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <i data-feather="clock"></i>
                      <div>
                        <span className="label">Last Visit</span>
                        <span className="value">
                          {selectedPatient.appointments && selectedPatient.appointments.length > 0 
                            ? new Date(selectedPatient.appointments[0].appointmentDate).toLocaleDateString()
                            : 'No visits recorded'
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section full-width">
                    <h4>Current Visit</h4>
                    <div className="detail-item">
                      <i data-feather="clipboard"></i>
                      <div>
                        <span className="label">Reason for Visit</span>
                        <span className="value">{selectedPatient.reasonForVisit}</span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <i data-feather="thermometer"></i>
                      <div>
                        <span className="label">Symptoms</span>
                        <span className="value">{selectedPatient.symptoms}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section full-width">
                    <h4>Medical History</h4>
                    <div className="medical-history">
                      <p>{selectedPatient.medicalHistory}</p>
                    </div>
                  </div>
                </div>

                <div className="patient-actions">
                  <button className="btn btn-primary">
                    <i data-feather="file-plus"></i>
                    Add Notes
                  </button>
                  <button className="btn btn-secondary">
                    <i data-feather="file-text"></i>
                    Generate Prescription
                  </button>
                  <button className="btn btn-outline">
                    <i data-feather="clipboard"></i>
                    Order Lab Tests
                  </button>
                  <button className="btn btn-outline" onClick={() => setShowPatientDetails(false)}>
                    <i data-feather="x"></i>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorDashboard; 